// The Sky team dashboards (#1489, #1694): sentiment within Sky-relevant and
// Sky-attributed comments, the Source row (Social / Influencer), and the
// Admin view where Sky's influencer export is uploaded. Opened on a Sky team
// page (`/sky-social-media/brand?walkthrough=sky-team-dashboards`). It shows
// the upload's file picker and never chooses a file or confirms an import.

import type { Walkthrough } from './types'

export const skyTeamDashboards: Walkthrough = {
  slug: 'sky-team-dashboards',
  title: 'Sky team dashboards',
  summary:
    "Shows the tiles Sky's template asked for, switches on influencer data beside Sky's own posts, and opens the page where a new influencer file is uploaded. Nothing is uploaded or saved.",
  issue: '#1489',
  added: '2026-10-20',
  skipE2E: "starts on a Sky team page (/sky-social-media/:product) with Sky's Sprinklr data; the spec opens Tables on the fixture job",
  steps: [
    {
      route: 'sky-social-media-product',
      feature: 'autocoder/social-media-dashboards',
      say: "This is a Sky team page. It follows Sky's template: volume and reach first, then sentiment, then the trackers over time.",
    },
    {
      route: 'sky-social-media-product',
      feature: 'autocoder/key-metrics',
      say: "Relevant sentiment and Attributed sentiment are the share of positive comments among comments about Sky, and among comments that credit Sky. They are counted on those comments alone, not on every comment.",
    },
    {
      route: 'sky-social-media-product',
      feature: 'autocoder/charts',
      say: 'Further down, two line charts track that sentiment month by month: within relevant comments, and within attributed ones. The share of comments that are relevant or attributed is in Breakdowns.',
    },
    {
      route: 'sky-social-media-product',
      feature: 'autocoder/source-discipline',
      click: 'Influencer',
      control: 'Influencer',
      say: "I chose Influencer in the Source row, under Platform. Every panel now shows only creators' posts from Sky's influencer file, and their scraped comments.",
    },
    {
      route: 'sky-social-media-product',
      feature: 'autocoder/source-discipline',
      click: 'All sources',
      control: 'All sources',
      say: 'All puts Social and Influencer back together. Each option shows its posts and comments, as the platforms do.',
    },
    {
      route: 'sky-social-media-product',
      feature: 'autocoder/post-data',
      click: 'Social media admin',
      say: 'Admin is for Realm admins and Managers. It opens on Post data, where one post’s numbers are corrected.',
    },
    {
      route: 'sky-social-media-entities',
      feature: 'autocoder/influencer-data',
      click: 'Influencer data',
      say: 'Influencer data is where a new influencer export is uploaded.',
    },
    {
      route: 'sky-social-media-influencer-data',
      feature: 'autocoder/influencer-data',
      control: 'Choose file',
      say: 'Choose file takes an .xlsx or .csv with the same columns as Sky’s export. You see a preview first: posts per team, new, updated and unchanged, and every duplicate listing folded into one post.',
    },
    {
      route: 'sky-social-media-influencer-data',
      feature: 'autocoder/influencer-data',
      say: 'Nothing is written until you confirm. An upload adds new posts and updates the ones already held; it never deletes. A file with an unknown team or a missing column is refused whole. Uploads are listed below.',
    },
  ],
}
